/*
 * /classes/:slug/classmates — "Friends": chọn một bạn cùng khoá, đặt hai bộ sưu tập badge cạnh nhau.
 * API: GET /api/classes/:slug/classmates → { classmates: [{ id, full_name, avatar_url, badges[], bio, school_name, favorite_* }] }
 */
import { useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Icon, type AnyIconName } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Page, PageHeader, UserAvatar, usePageTitle } from "@/components/page"
import { EditorialVisual } from "@/components/simba"
import { useClass } from "@/app/ClassLayout"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { BadgeGrid, Disclosure } from "./community/shared"

function Collection({ label, person, badges, empty, mine }: { label: string; person: any; badges: any[]; empty: string; mine?: boolean }) {
  return (
    <Card className={cn(mine && "border-orange/40")}>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <UserAvatar user={person} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
            <h2 className="truncate font-heading text-lg font-semibold text-ink">{person.full_name}</h2>
          </div>
          <span className="grid min-w-12 place-items-center rounded-2xl bg-soft px-3 py-1.5 font-heading text-xl font-bold text-orange-strong">{badges.length}</span>
        </div>
        <BadgeGrid badges={badges} empty={empty} />
      </CardContent>
    </Card>
  )
}

function Fact({ icon, label, value }: { icon: AnyIconName; label: string; value?: string }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-sand/60 p-3">
      <Icon name={icon} className="mt-0.5 size-5 text-orange" />
      <div className="min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-semibold break-words text-ink">{value}</p>
      </div>
    </div>
  )
}

export default function ClassmatesPage() {
  usePageTitle("Bạn học", "So sánh bộ sưu tập badge với các bạn cùng khóa.")
  const { slug, data: classData } = useClass()
  const [data, setData] = useState<any>()
  const [selectedId, setSelectedId] = useState("")
  const [error, setError] = useState("")

  const load = () => {
    setError("")
    api(`/api/classes/${encodeURIComponent(slug)}/classmates`)
      .then((next: any) => {
        setData(next)
        setSelectedId((cur) => cur || next.classmates[0]?.id || "")
      })
      .catch((e: any) => setError(e.message))
  }
  useEffect(() => { load() }, [slug])

  const learner = classData?.learner || {}
  const myBadges = (classData?.stickers || []).filter((b: any) => b.earned)

  if (error) return <Page><ErrorState message={error} onRetry={load} /></Page>
  if (!data) return <LoadingState label="Đang tìm các bạn cùng khóa…" />

  const classmates: any[] = data.classmates || []
  const selected = classmates.find((c) => c.id === selectedId)

  return (
    <Page>
      <div className="grid items-center gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <PageHeader eyebrow="Bạn học" title={<>So sánh <em className="text-orange not-italic">badge</em></>} description="Chọn một bạn để đặt hai bộ sưu tập cạnh nhau." />
        <EditorialVisual kind="community" alt="Hai hành trình học tập gặp nhau bên một bộ sưu tập thành quả" className="hidden lg:block" />
      </div>

      {classmates.length === 0 ? (
        <EmptyState mascot="meerkat-thinking" title="Chưa có bạn học nào" description="Khi có bạn cùng tham gia khóa học, danh sách sẽ xuất hiện ở đây." />
      ) : (
        <>
          <section aria-label="Chọn bạn để so sánh" className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-muted-foreground">So sánh với</p>
            <ul className="flex flex-wrap gap-2">
              {classmates.map((c) => {
                const active = c.id === selectedId
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      aria-pressed={active}
                      onClick={() => setSelectedId(c.id)}
                      className={cn(
                        "flex items-center gap-2 rounded-full border-[1.5px] bg-card py-1.5 pr-3 pl-1.5 text-sm font-semibold transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
                        active ? "border-orange bg-soft text-orange-strong" : "border-edge text-ink hover:border-orange",
                      )}
                    >
                      <UserAvatar user={c} size="sm" />
                      <span className="max-w-40 truncate">{c.full_name}</span>
                      <Badge variant={active ? "solid" : "default"} className="h-6">{c.badges.length}</Badge>
                    </button>
                  </li>
                )
              })}
            </ul>
          </section>

          {selected && (
            <>
              <section aria-label="So sánh badge" className="grid gap-4 md:grid-cols-2">
                <Collection mine label="Bộ sưu tập của tôi" person={learner} badges={myBadges} empty="Con chưa mở badge nào." />
                <Collection label="Bộ sưu tập của bạn" person={selected} badges={selected.badges} empty="Bạn ấy đang trên đường mở badge đầu tiên." />
              </section>
              {(selected.bio || selected.school_name || selected.favorite_food || selected.favorite_movie || selected.favorite_music) && (
                <Disclosure title={`Xem thêm về ${selected.full_name}`}>
                  <div className="flex flex-col gap-3">
                    {selected.bio && <blockquote className="rounded-2xl border-l-4 border-orange bg-soft/60 px-4 py-3 text-base text-ink">{selected.bio}</blockquote>}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Fact icon="graduation" label="Trường" value={selected.school_name} />
                      <Fact icon="heart" label="Đồ ăn yêu thích" value={selected.favorite_food} />
                      <Fact icon="play-circle" label="Phim yêu thích" value={selected.favorite_movie} />
                      <Fact icon="volume" label="Band / nhóm nhạc" value={selected.favorite_music} />
                    </div>
                  </div>
                </Disclosure>
              )}
            </>
          )}
        </>
      )}
    </Page>
  )
}
